import { Map } from 'immutable';
import React, {
  PureComponent,
  createContext,
  FunctionComponent,
  ReactNode,
  useMemo,
  useState,
  useCallback,
} from 'react';
import FluentdLogger from '../../services/fluentdLogger';
import Logger from '../../services/logger';
import { isShallowEqual } from '../../utils/object';

interface ILazyCacheItem<T> { value?: T; error?: Error; }
type LazyCache = Map<symbol, ILazyCacheItem<any>>;

interface ICacheContext {
  cache: LazyCache;
  update(cacheKey: symbol, load: () => Promise<any>): void;
}

const { Provider, Consumer } = createContext<ICacheContext | undefined>(undefined);

/**
 * Provides a global cache for lazy-loaded components. This is
 * necessary so that `<Lazy>` components don't render the loading state
 * if the component was already loaded.
 *
 * This component should sit as close to the root of the component tree
 * as possible, maybe only wrapped with an error boundary.
 */
export const LazyCacheProvider: FunctionComponent = ({
  children,
}) => {
  const [cache, setCache] = useState<LazyCache>(Map());
  const update = useCallback(async (cacheKey: symbol, load: () => Promise<any>) => {
    try {
      const cacheValue = await load();
      setCache((lastCache) =>
        !isShallowEqual(lastCache.get(cacheKey), { value: cacheValue })
          ? lastCache.set(cacheKey, { value: cacheValue })
          : lastCache,
      );
    } catch (error) {
      // Log the error to the console for local development purposes.
      Logger.error(error);
      // Send error to Fluentd so we can detect consistent errors.
      FluentdLogger.warn({ name: 'Lazy.load', message: error && error.message });

      setCache((lastCache) =>
        !isShallowEqual(lastCache.get(cacheKey), { error })
          ? lastCache.set(cacheKey, { error })
          : lastCache,
      );
    }
  }, [setCache]);
  const value = useMemo(() => ({
    update,
    cache,
  }), [update, cache]);
  return <Provider value={value}>{children}</Provider>;
};

interface ILazyProps<T> {
  staticCacheKey: symbol;
  load: () => Promise<T>;
  children: (params: ILazyCacheItem<T>) => ReactNode;
}

/**
 * Lazy-loads modules with the `load()` prop which is usually going to
 * contain `import(...)` expression. The returned value is arbitrary
 * (doesn't need to be a component). Value or error are passed to
 * `children` as `{ value, error }` argument. When both values are
 * undefined it means the module is being loaded.
 *
 * It uses the cache provided by `<LazyCacheProvider>`, values are
 * keyed by `staticCacheKey` which needs to be a unique `Symbol`,
 * preferably static, defined in your module. If the value (or error)
 * is in the cache, it will render with the value straight away but it
 * will still trigger `load` to check if the value has been updated
 * (only useful in HMR scenarios).
 */
export function Lazy<T>({ staticCacheKey, load, children }: ILazyProps<T>) {
  return <Consumer>{(context) => {
    if (!context) return null;

    const { update, cache } = context;
    const args = (cache.get(staticCacheKey) || {}) as ILazyCacheItem<T>;

    return <>
      <Trigger staticCacheKey={staticCacheKey} load={load} update={update} />
      {children(args)}
    </>;
  }}</Consumer>;
}

interface ITriggerProps {
  staticCacheKey: symbol;
  load: () => Promise<any>;
  update: (key: symbol, load: () => Promise<any>) => void;
}

class Trigger extends PureComponent<ITriggerProps> {
  public componentDidMount() {
    this.load();
  }

  public componentDidUpdate(prevProps: ITriggerProps) {
    // This has a possible shortcoming with HMR: depends on the
    // component importing the lazy module, instead of the one being
    // imported, a reload might be necessary
    if (this.props.staticCacheKey !== prevProps.staticCacheKey) {
      this.load();
    }
  }

  public render() {
    return null;
  }

  private readonly load = () => {
    const { load, update, staticCacheKey } = this.props;
    update(staticCacheKey, load);
  };
}

export function makeLazyLoader<T>(load: () => Promise<T>) {
  const staticCacheKey = Symbol(String(load));

  return (props: Omit<ILazyProps<T>, 'staticCacheKey' | 'load'>) =>
    <Lazy staticCacheKey={staticCacheKey} load={load} {...props} />;
}
